import {
  createPrivateAPI,
  DocumentServices,
  HostAPI,
  HostType,
  IPrivateAPI,
  LimitedPrivateAPI,
  PlatformPrivateServices,
} from '@wix/editor-platform-private-api';

import {
  DMEventsBridge,
  PlatformLifecycleEvent,
  PlatformPrivateEventEmitter,
} from '@wix/public-editor-platform-events';
import { ExperimentsAPI } from '@wix/editorx-core-api';
import {
  PlatformWorkerManager,
  SingleWorkerStrategy,
} from './PlatformWorkerManager';
import { AppRunner } from './PlatformWorkerManager/AppRunner';
import { getWorkerUrl } from './PlatformWorkerManager/getWorkerUrl';
import { PlatformFrameStrategy } from './PlatformWorkerManager/PlatformFrameStrategy';

import { ManifestServicesHost } from '@wix/editor-platform-manifest-services';

import { IPlatformInfraPublicAPI } from './types';
import { PlatformDevTools } from './PlatformDevTools';
import { PlatformSDKHost } from './PlatformSDKHost';
import { IEditorPlatformPackagesBridge } from '@wix/editor-platform-interfaces';

function subscribeOnHostEvents(ps: PlatformPrivateServices) {
  const dmEventsBridge = new DMEventsBridge(ps.events);

  const callback = (event: any) => {
    dmEventsBridge.notify(event);
  };

  ps.ds.platform.subscribeOnAppEvents(callback);
  ps.ds.platform.subscribeOnCustomAppEvents(callback);
}

/**
 * TODO: move this class somewhere other package
 * probably do it as soon as we move to the new repo with the DI
 */
export class PlatformInfra {
  private readonly ps: PlatformPrivateServices;
  private readonly workerManager: PlatformWorkerManager;
  private readonly platformSDKHost: PlatformSDKHost;

  private readonly privateAPI: IPrivateAPI;
  private readonly limitedPrivateAPI: LimitedPrivateAPI;

  constructor(
    private hostType: HostType,
    private apis: {
      hostAPI: HostAPI;
      documentServicesAPI: DocumentServices;
      experimentsAPI: ExperimentsAPI;
      editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge>;
    },
  ) {
    const platformEvents = new PlatformPrivateEventEmitter();

    const manifestServicesHost = new ManifestServicesHost(
      this.apis.documentServicesAPI,
      this.apis.hostAPI,
      this.apis.experimentsAPI,
      platformEvents,
      this.apis.editorPlatformPackagesBridge,
      {
        getSDKHostContext: (...args) =>
          this.platformSDKHost.getSDKHostContext.apply(
            this.platformSDKHost,
            args,
          ),
        getSDKAuth: (...args) =>
          this.platformSDKHost.getSDKAuth.apply(this.platformSDKHost, args),
      },
    );

    this.ps = new PlatformPrivateServices(
      this.apis.documentServicesAPI,
      this.apis.hostAPI,
      platformEvents,
      this.apis.experimentsAPI,
      manifestServicesHost,
      this.apis.editorPlatformPackagesBridge,
    );

    this.privateAPI = createPrivateAPI(this.hostType, this.ps);

    this.platformSDKHost = new PlatformSDKHost(
      this.privateAPI,
      this.ps.manifestServicesHost,
    );

    this.limitedPrivateAPI = new LimitedPrivateAPI(this.privateAPI);

    const workerStrategy = new SingleWorkerStrategy(
      getWorkerUrl,
      this.limitedPrivateAPI,
      platformEvents,
    );

    const frameStrategy = new PlatformFrameStrategy(
      this.ps,
      this.limitedPrivateAPI,
      platformEvents,
    );

    this.workerManager = new PlatformWorkerManager(
      this.privateAPI,
      workerStrategy,
      frameStrategy,
      platformEvents,
    );

    if (this.isDebugMode()) {
      const devtools = new PlatformDevTools(
        this.getPublicAPI(),
        this.ps.manifestServicesHost,
        this.apis.editorPlatformPackagesBridge.EditorPointerAPI!,
      );
      devtools.register();
    }
  }

  isDebugMode() {
    return (
      typeof window !== 'undefined' &&
      typeof window.location !== 'undefined' &&
      new URLSearchParams(window.location.search).has('debug')
    );
  }

  getPublicAPI(): IPlatformInfraPublicAPI {
    return {
      getSDKHost: this.platformSDKHost.getSDKHost.bind(this.platformSDKHost),
      getSDKHosts: this.platformSDKHost.getSDKHosts.bind(this.platformSDKHost),
      getSDKAuth: this.platformSDKHost.getSDKAuth.bind(this.platformSDKHost),
      getSDKHostContext: this.platformSDKHost.getSDKHostContext.bind(
        this.platformSDKHost,
      ),
      getPlatformSDKHost: this.platformSDKHost.getSDKHost.bind(
        this.platformSDKHost,
      ),
      connectFrame: this.connectFrame.bind(this),
      getComponentsService: this.getComponentsService.bind(this),
    };
  }

  getComponentsService() {
    return this.ps.manifestServicesHost.getComponentsService();
  }

  public async connectFrame(
    appDefinitionId: string,
    iframe: HTMLIFrameElement,
  ) {
    return this.workerManager.connectFrame(appDefinitionId, iframe);
  }

  startManifestServices() {
    this.ps.manifestServicesHost.start();
  }

  async startApps() {
    const runner = new AppRunner(this.ps, this.workerManager);
    await runner.start();

    subscribeOnHostEvents(this.ps);

    this.workerManager.events.notify({
      type: PlatformLifecycleEvent.EditorReady,
      payload: {},
      meta: {},
    });
  }
}
